/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTitle: Full Stack Developer
 *
 * Device step validator.
 * Centralizes all per-section validation so components only manage emptyFields display.
 *
 * Usage:
 *   import { STEP_TO_SECTION, deviceValidator } from './deviceValidator';
 *
 *   // On Next button click:
 *   const handleValidateSteps = () => {
 *     const sections = STEP_TO_SECTION[activeStep] ?? [];
 *     return sections.every(section =>
 *       deviceValidator.isValid(section, "step", { data, options, security, authentication })
 *     );
 *   };
 *
 *   // On final submit:
 *   const handleValidateSubmit = () => {
 *     const requiredSections = sectionsByDeviceType[data.deviceType] ?? [];
 *     return requiredSections.every(section =>
 *       deviceValidator.isValid(section, "submit", { data, options, security, authentication })
 *     );
 *   };
 */

import {ANTIPASSBACKTYPE_MODEL, DEVICE_MODEL_NAME, DISABLE_CAMERA_ANTENNA, STEPS_CREATE_DEVICE,} from './Model';

/** Devices where distance is disabled by default */
export const DISABLE_DISTANCE = [
    DEVICE_MODEL_NAME.CAMERA_LPR,
    DEVICE_MODEL_NAME.ANTENNA_RFID,
    DEVICE_MODEL_NAME.TELPO_TPS_450
];
/** Devices where Relay0 fields (timeForRelay0, delayToOpen) are required */
export const ENABLE_RELAY0_REQUIRED = [
    DEVICE_MODEL_NAME.TELPO_F10,
    DEVICE_MODEL_NAME.ARATEK_BA8300,
];
/**
 * Maps each wizard step index to the section number(s) in isValid.
 * If a step renders multiple sections, list all section numbers.
 */
export const STEP_TO_SECTION = {
    [STEPS_CREATE_DEVICE.DATA.INDEX]:           [1],
    [STEPS_CREATE_DEVICE.SECURITY.INDEX]:       [2],
    [STEPS_CREATE_DEVICE.OPTIONS.INDEX]:        [3],
    [STEPS_CREATE_DEVICE.AUTHENTICATION.INDEX]: [5, 6],
    [STEPS_CREATE_DEVICE.RELAY.INDEX]:          [4],
};

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/**
 * Returns true if distance is currently active and the device supports it.
 * @param {string} deviceType
 * @param {boolean} enableDistance
 */
const isDistanceAllowed = (deviceType, enableDistance) => {
    if (!enableDistance) return false;
    return !DISABLE_DISTANCE.includes(deviceType)
}
// ---------------------------------------------------------------------------
// Main validator
// ---------------------------------------------------------------------------

/**
 * Validates a specific device form section.
 *
 * Available sections:
 * - 1: General data.
 * - 2: Security configuration.
 * - 3: Device options.
 * - 4: Relay configuration.
 * - 5: Distance configuration.
 * - 6: Consecutive openings configuration.
 *
 * @function isValid
 *
 * @param {number} section - Validation section identifier.
 * @param {"step"|"submit"} [mode="submit"] - Validation mode.
 * @param {Object} [state={}] - Full form state.
 * @param {Object} state.data - Device data state.
 * @param {Object} state.options - Device options state.
 * @param {Object} state.security - Security configuration state.
 * @param {Object} state.authentication - Authentication configuration state.
 * @param {Object} state.relay - Relay configuration state.
 *
 * @returns {boolean} Validation result.
 */
export const isValid = (section, mode = "submit", state = {}) => {
    const { data = {}, options = {}, security = {}, authentication = {}, relay = {} } = state;

    const validations = {
        1: () => {
            const fields = {
                deviceType:  !!data.deviceType,
                deviceName:  !!data.deviceName,
                modeId:      !!data.modeId,
                sn:          !!data.sn,
                ip:          !!data.ip,
                mac:         !!data.mac,
                statusValue: !!data.statusValue,
            };

            const isCameraOrAntenna = [
                DEVICE_MODEL_NAME.CAMERA_LPR,
                DEVICE_MODEL_NAME.ANTENNA_RFID
            ].includes(data.deviceType);

            const passwordValid = isCameraOrAntenna
                ? true
                : (
                    data.password !== null &&
                    data.password !== undefined &&
                    data.password !== '' &&
                    data.password.length >= 8
                );

            return Object.values(fields).every(Boolean) && passwordValid;
        },

        2: () => {
            if (DISABLE_CAMERA_ANTENNA.includes(data.deviceType)) return true;

            const isSoftOrHard =
                security.antiPassBackType === ANTIPASSBACKTYPE_MODEL.SOFT ||
                security.antiPassBackType === ANTIPASSBACKTYPE_MODEL.HARD;

            if (isSoftOrHard) {
                const timeValid =
                    security.antiPassBackTime !== 0 &&
                    security.antiPassBackTime !== '' &&
                    security.antiPassBackTime !== null &&
                    security.antiPassBackTime !== undefined;

                const alertValid = !!security.antiPassBackAlert;

                return timeValid && alertValid;
            }

            return (
                security.antiPassBackType !== null &&
                security.antiPassBackType !== undefined &&
                security.antiPassBackType !== ''
            );
        },
        3 : () => {
            return DISABLE_CAMERA_ANTENNA.includes(data.deviceType)
                ? true
                : options.timeZoneValue !== null && options.timeZoneValue !== '';
        },
        4: () => {
            const isEnabledDevice = [
                DEVICE_MODEL_NAME.TELPO_F10,
                DEVICE_MODEL_NAME.ARATEK_BA8300
            ].includes(data?.deviceType);

            const keepOpenRelayValid = isEnabledDevice
                ? Number(relay.keepOpenRelay) >= 1
                : Number(relay.keepOpenRelay) === 0;

            const delayToOpenValid =
                relay.delayToOpen !== null &&
                relay.delayToOpen !== undefined &&
                relay.delayToOpen !== '' &&
                Number(relay.delayToOpen) >= 0;

            return keepOpenRelayValid && delayToOpenValid;
        },

        5: () => {
            const distanceActive = isDistanceAllowed(data.deviceType, authentication.enableDistance);

            return  distanceActive
                ? authentication.distance !== null &&
                authentication.distance !== '' &&
                Number(authentication.distance) !== 0
                : true;
        },

        6: () => {
            if (DISABLE_CAMERA_ANTENNA.includes(data.deviceType)) return true;

            const readAfterValid =
                authentication.readAfter !== null &&
                authentication.readAfter !== undefined &&
                authentication.readAfter !== '' &&
                Number(authentication.readAfter) >= 1;

            const consecutiveValid =
                authentication.consecutiveOpeningsAllowed !== null &&
                authentication.consecutiveOpeningsAllowed !== undefined &&
                authentication.consecutiveOpeningsAllowed !== '';

            return readAfterValid && consecutiveValid;
        },
    };

    return validations[section] ? validations[section]() : false;
};

// ---------------------------------------------------------------------------
// Convenience: validate all sections for a given step
// ---------------------------------------------------------------------------

/**
 * Validates all sections mapped to the given wizard step index.
 *
 * @param {number} stepIndex - activeStep value
 * @param {object} state     - { data, options, security, authentication }
 * @returns {boolean}
 */
export const isStepValid = (stepIndex, state) => {
    const sections = STEP_TO_SECTION[stepIndex] ?? [];
    return sections.every(section => isValid(section, "step", state));
};
